import { usePlanStore } from './plan'
import Plan from '@/classes/Plan'
import { stateRequiresSignature } from '@/components/documents/CompleteStep/stateSignaturesConfig'
import { DocumentType } from '@/enums/DocumentType'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useCrmStore } from '@/stores/crm'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import type {
	MissingSignaturesBySigner,
	SignerSelection,
	StoreDocument,
} from '@/types'
import { defineStore } from 'pinia'
import { computed, ref, toRaw, watch } from 'vue'

export type PageStatusType =
	| 'pending'
	| 'ready'
	| 'merging'
	| 'complete'
	| 'switch'
	| 'first-load'
	| 'confirmation'
	| 'Payment Authorization'
	| 'OAP Enrollment'
	| 'Everplans Enrollment'

// autofill indicates whether the PDF has been automatically filled by plan data
export type Step = {
	label: PageStatusType | DocumentType
	title: string
	type: 'html' | 'document'
	completed: boolean
	valid: boolean
	active: boolean
	autofill: boolean
}

/**
 * Pinia store responsible for determining the state of the Signing Flow
 * setting and storing the steps, saving document blobs, and setting the page state
 */
export const useSessionDocumentsStore = defineStore('sessionDocuments', () => {
	const sessionStore = useSessionStore()
	const planStore = usePlanStore()
	const documentStore = useDocumentStore()
	const authStore = useAuthStore()
	const crmStore = useCrmStore()
	const cartStore = useCartStore()
	const currentDocument = ref<StoreDocument>()
	const currentPlanHolderID = ref<string>('')
	const missingSignatures = ref<Record<string, MissingSignaturesBySigner[]>>({})
	const selectedSigners = ref<Record<string, SignerSelection>>({})
	const useAlternateFormClass = ref(true)
	const hasAlternateFormClass = ref(false)

	const pageState = ref<{
		status: PageStatusType
		meta?: Record<any, any>
	}>({ status: 'ready' })
	const steps = ref<Record<string, Step[]>>({})
	const supplementalSteps = ref<Record<string, Step[]>>({})

	// computed for the insurance payment type
	const insurancePaymentType = computed(
		() => planStore.getPlan(currentPlanHolderID.value)?.insurance.paymentType
	)

	// computed for whether the steps are ready
	const stepsReady = computed(() =>
		Boolean(steps.value[currentPlanHolderID.value])
	)

	// computed for the current plan
	const currentPlan = computed(() =>
		sessionStore.getPlanHolder(currentPlanHolderID.value)
	)

	// computed for whether the current plan is encrypted
	const currentPlanIsEncrypted = computed(
		() =>
			!!(
				currentPlan.value &&
				currentPlan.value.encryptionStatus !== 'unencrypted'
			)
	)

	const planHolderIsEncrypted = (planHolderID: string) => {
		const plan = planStore.getPlan(planHolderID)

		if (!plan) {
			return false
		}

		return (
			plan.encryptionStatus !== undefined &&
			plan.encryptionStatus !== 'unencrypted'
		)
	}

	// Used to set the page state
	const setPageState = async (
		state: PageStatusType,
		meta?: Record<any, any>
	) => {
		pageState.value = { status: state, meta }
	}

	// Used to clear the current document
	const clearCurrentDocument = () => {
		currentDocument.value = undefined
	}

	// Used to set a PDF form as the current document
	const setCurrentDocument = (
		planHolderId: string,
		documentType: DocumentType
	) => {
		const planHolder = sessionStore.getPlanHolder(planHolderId)

		if (!planHolder) {
			throw new Error(`Plan holder ${planHolderId} not found`)
		}

		const documentId = planHolder.documents.find(
			(document) => document.type === documentType
		)?.id

		if (!documentId) {
			throw new Error(`Document ${documentType} not found for ${planHolderId}`)
		}

		currentDocument.value = documentStore.getPlanHolderDocumentById(documentId)
	}

	// Used to specify which planHolder is currently being edited.
	const setCurrentPlanHolderID = (planHolderID: string) => {
		currentPlanHolderID.value = planHolderID
	}

	// Used to set whether to display the HTML or document form of the payment step of the given plan holder.
	// The payment step is used to determine which component to display in the PaymentStep component.
	const setPaymentStep = async (
		planHolderID: string,
		step: 'html' | 'document',
		loadFile: boolean = true
	) => {
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			return
		}

		// When setPaymentStep is called because of payment Type or Term changed, we don't want to load the file.
		if (loadFile) {
			await setPageState('pending')
		}

		if (step === 'html') {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === 'Payment Authorization'
				),
				1,
				{
					label: DocumentType.PaymentAuthorization,
					title: 'document.paymentAuthorization',
					type: 'html',
					completed: false,
					valid: true,
					active: loadFile,
					autofill: false,
				}
			)
			if (!loadFile) return
			await setPageState('Payment Authorization')
		} else {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === 'Payment Authorization'
				),
				1,
				{
					label: DocumentType.PaymentAuthorization,
					title: 'document.paymentAuthorization',
					type: 'document',
					completed: false,
					valid: false,
					active: loadFile,
					autofill: false,
				}
			)

			if (!loadFile) return

			await setPageState('switch', {
				planHolderID,
				document: documentStore.getPlanHolderDocumentByType(
					planHolder,
					'Payment Authorization'
				),
			})
		}
	}

	// Used to set whether to display the HTML or document form of the OAP step of the given plan holder.
	// The payment step is used to determine which component to display in the OapStep component.
	const setOapEnrollmentStep = async (
		planHolderID: string,
		step: 'html' | 'document'
	) => {
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			return
		}

		await setPageState('pending')

		if (step === 'html') {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === 'OAP Enrollment'
				),
				1,
				{
					label: DocumentType.OAPEnrollment,
					title: 'document.oapEnrollment',
					type: 'html',
					completed: false,
					valid: true,
					active: true,
					autofill: false,
				}
			)
			await setPageState('OAP Enrollment')
		} else {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === 'OAP Enrollment'
				),
				1,
				{
					label: DocumentType.OAPEnrollment,
					title: 'document.oapEnrollment',
					type: 'document',
					completed: false,
					valid: false,
					active: true,
					autofill: false,
				}
			)

			await setPageState('switch', {
				planHolderID,
				document: documentStore.getPlanHolderDocumentByType(
					planHolder,
					'OAP Enrollment'
				),
			})
		}
	}

	// Used to set whether to display the HTML or document form of the Everplans step of the given plan holder.
	const setEverplansPaymentStep = async (
		planHolderID: string,
		step: 'html' | 'document'
	) => {
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			return
		}

		await setPageState('pending')

		if (step === 'html') {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === DocumentType.EverplansEnrollment
				),
				1,
				{
					label: DocumentType.EverplansEnrollment,
					title: 'document.everplansEnrollment',
					type: 'html',
					completed: false,
					valid: true,
					active: true,
					autofill: false,
				}
			)
			await setPageState(DocumentType.EverplansEnrollment)
		} else {
			steps.value[planHolderID].splice(
				steps.value[planHolderID].findIndex(
					(s) => s.label === DocumentType.EverplansEnrollment
				),
				1,
				{
					label: DocumentType.EverplansEnrollment,
					title: 'document.everplansEnrollment',
					type: 'document',
					completed: false,
					valid: false,
					active: true,
					autofill: false,
				}
			)

			await setPageState('switch', {
				planHolderID,
				document: documentStore.getPlanHolderDocumentByType(
					planHolder,
					DocumentType.EverplansEnrollment
				),
			})
		}
	}

	// Used to set the required steps and supplemental steps, called on a session load
	const setSteps = async () => {
		if (!sessionStore.activePlans?.length) return

		for (const planHolder of sessionStore.activePlans) {
			steps.value[planHolder.id] = []
			supplementalSteps.value[planHolder.id] = []

			steps.value[planHolder.id].push({
				label: 'confirmation',
				title: 'document.confirmInformation',
				type: 'html',
				completed: planHolder.completedSteps.includes('confirmation'),
				valid: false,
				active: false,
				autofill: false,
			})

			const allDocuments = documentStore.getPlanHolderDocuments(planHolder)

			if (!allDocuments || allDocuments.length === 0) {
				return
			}

			const requiredDocuments = allDocuments.filter(
				(document) =>
					!document.meta.supplemental &&
					document.meta.type !== DocumentType.MergedDocument
			)
			const supplementalDocuments = allDocuments.filter(
				(document) => document.meta.supplemental
			)

			if (requiredDocuments && requiredDocuments!.length > 0) {
				requiredDocuments.forEach((document) => {
					if (document.meta.type === DocumentType.PaymentAuthorization) {
						steps.value[planHolder.id].push({
							label: DocumentType.PaymentAuthorization,
							title: 'document.paymentAuthorization',
							type: planHolder.paymentStep,
							completed: planHolder.completedSteps.includes(
								'Payment Authorization'
							),
							valid: false,
							active: false,
							autofill: false,
						})
					} else if (
						document.meta.type === DocumentType.OAPEnrollment &&
						planHolder.oapIncluded
					) {
						steps.value[planHolder.id].push({
							label: DocumentType.OAPEnrollment,
							title: 'document.oapEnrollment',
							type: planHolder.oapStep,
							completed: planHolder.completedSteps.includes(
								DocumentType.OAPEnrollment
							),
							valid: false,
							active: false,
							autofill: false,
						})
					} else if (
						document.meta.type === DocumentType.EverplansEnrollment &&
						planHolder.everplansIncluded &&
						authStore.isEverplansAuthorized &&
						!cartStore.cart[planHolder.id]?.includesFreeEverplansPremium
					) {
						steps.value[planHolder.id].push({
							label: DocumentType.EverplansEnrollment,
							title: 'document.everplansEnrollment',
							type: planHolder.everplansStep,
							completed: planHolder.completedSteps.includes(
								DocumentType.EverplansEnrollment
							),
							valid: false,
							active: false,
							autofill: false,
						})
					} else if (
						document.meta.type !== DocumentType.OAPEnrollment &&
						document.meta.type !== DocumentType.EverplansEnrollment
					) {
						steps.value[planHolder.id].push({
							label: document.meta.type,
							title: `document.${getTranslationKey(document.meta.type)}`,
							type: 'document',
							completed: planHolder.completedSteps.includes(document.meta.type),
							valid: false,
							active: false,
							autofill: false,
						})
					}
				})
			}

			if (supplementalDocuments && supplementalDocuments!.length > 0) {
				supplementalDocuments.forEach((document) => {
					supplementalSteps.value[planHolder.id].push({
						label: document.meta.type,
						title: `document.${getTranslationKey(document.meta.type)}`,
						type: 'document',
						completed: planHolder.completedSteps.includes(document.meta.type),
						valid: false,
						active: false,
						autofill: false,
					})
				})
			}
		}
	}

	// Used to get the correct key for translations, this method turns value in DocumentType to en.json key values
	const getTranslationKey = (documentType: string) => {
		let key = documentType
			.split(' ')
			.map((word) => word[0].toUpperCase() + word.slice(1))
			.join('')
		return key[0].toLowerCase() + key.slice(1)
	}

	// Used to get the completion status for the current user's confirmation step
	// which will enable/disable the rest of the steps.
	const isConfirmationCompleted = (planHolderId: string) => {
		return steps.value[planHolderId].find((s) => s.label === 'confirmation')
			?.completed
	}

	const isEverplansCompleted = (planHolderId: string) => {
		return steps.value[planHolderId].find(
			(s) => s.label === DocumentType.EverplansEnrollment
		)?.completed
	}

	// The current step is now computed based on whether the step's active property is true
	const currentStep = computed(() => {
		if (
			!currentPlanHolderID.value ||
			!steps.value[currentPlanHolderID.value] ||
			!supplementalSteps.value[currentPlanHolderID.value]
		) {
			return null
		}

		let activeStep = steps.value[currentPlanHolderID.value].find(
			(s) => s.active
		)

		if (!activeStep) {
			activeStep = supplementalSteps.value[currentPlanHolderID.value].find(
				(s) => s.active
			)
		}
		return activeStep
	})

	// This method sets a step's active property to be true, which makes it the current step
	const setStepActive = (planHolderId: string, step: Step) => {
		setCurrentStepInactive()

		const stepIndex = steps.value[planHolderId].findIndex(
			(s) => s.label === step.label
		)

		if (stepIndex !== -1) {
			steps.value[planHolderId][stepIndex].active = true
		}

		const supplementalStepIndex = supplementalSteps.value[
			planHolderId
		].findIndex((s) => s.label === step.label)

		if (supplementalStepIndex !== -1) {
			supplementalSteps.value[planHolderId][supplementalStepIndex].active = true
		}
	}

	// This method sets the current step's inactive, can be called on complete step or when setting another step active
	const setCurrentStepInactive = () => {
		const currentStepIndex = steps.value[currentPlanHolderID.value].findIndex(
			(s) => s.active
		)

		if (currentStepIndex !== -1) {
			steps.value[currentPlanHolderID.value][currentStepIndex].active = false
		}

		const currentSupplementalStepIndex = supplementalSteps.value[
			currentPlanHolderID.value
		].findIndex((s) => s.active)

		if (currentSupplementalStepIndex !== -1) {
			supplementalSteps.value[currentPlanHolderID.value][
				currentSupplementalStepIndex
			].active = false
		}
	}

	// This method sets the current step to completed and updates the session store's completedSteps array
	const setStepCompleted = async (step: Step, completed: boolean) => {
		const stepIndex = steps.value[currentPlanHolderID.value].findIndex(
			(s) => s.label === step.label
		)
		if (stepIndex !== -1) {
			steps.value[currentPlanHolderID.value][stepIndex].completed = completed
		}

		if (supplementalSteps.value[currentPlanHolderID.value]) {
			const supplementalStepIndex = supplementalSteps.value[
				currentPlanHolderID.value
			].findIndex((s) => s.label === step.label)

			if (supplementalStepIndex !== -1) {
				supplementalSteps.value[currentPlanHolderID.value][
					supplementalStepIndex
				].completed = completed
			}
		}

		await planStore.updatePlan(currentPlanHolderID.value, {
			completedSteps: [
				...steps.value[currentPlanHolderID.value],
				...(supplementalSteps.value[currentPlanHolderID.value] ?? []),
			]
				.filter((s) => s.completed)
				.map((s) => s.label),
		})
	}

	// This method sets a given step's validation status
	const setStepValidated = (step: Step | undefined, valid: boolean) => {
		if (!step) return
		const index = steps.value[currentPlanHolderID.value].findIndex(
			(s) => s.label === step.label
		)
		if (index !== -1) {
			steps.value[currentPlanHolderID.value][index].valid = valid
		}

		const supplementalStepIndex = supplementalSteps.value[
			currentPlanHolderID.value
		].findIndex((s) => s.label === step.label)
		if (supplementalStepIndex !== -1) {
			supplementalSteps.value[currentPlanHolderID.value][
				supplementalStepIndex
			].valid = valid
		}
	}

	// This method sets a given step's autofill status
	const setStepAutoFilled = (step: Step | undefined, autofill: boolean) => {
		if (step) {
			const index = steps.value[currentPlanHolderID.value].findIndex(
				(s) => s.label === step.label
			)
			if (index !== -1) {
				steps.value[currentPlanHolderID.value][index].autofill = autofill
			}
		}
	}

	// This method will find the next Incomplete Step for the currentPlanHolder
	const getNextIncompleteStep = () => {
		return steps.value[currentPlanHolderID.value].find((s) => !s.completed)
	}

	// This method will find a step by its label for the current plan holder
	const getStepByLabel = (label: string) => {
		return steps.value[currentPlanHolderID.value].find((s) => s.label === label)
	}

	// This method will load the page with the next Incomplete Step
	const setNextIncompleteStepActive = () => {
		const nextIncompleteStep = getNextIncompleteStep()

		if (nextIncompleteStep) {
			setStepActive(currentPlanHolderID.value, nextIncompleteStep)
		} else {
			setStepActive(
				currentPlanHolderID.value,
				steps.value[currentPlanHolderID.value][0]
			)
		}
	}

	// This method will find the next Incomplete Step among all the planHolders and set the PageState accordingly
	const setPageStateOnLoad = async (planHolder: Plan | undefined) => {
		let pageStatus =
			!planHolder || planHolder.packetStatus !== PlanPacketStatus.Incomplete
				? 'complete'
				: 'confirmation'

		if (!planHolder || pageStatus === 'complete') {
			return await setPageState(pageStatus as PageStatusType, {})
		}

		setCurrentPlanHolderID(planHolder.id)
		const nextStep = getNextIncompleteStep()
		let meta = {}

		if (nextStep?.type === 'document') {
			pageStatus = 'switch'

			const document = documentStore.getPlanHolderDocumentByType(
				planHolder,
				nextStep?.label as DocumentType
			)

			meta = {
				planHolderID: planHolder.id,
				document,
			}
		} else {
			if (nextStep) {
				pageStatus = nextStep.label
			} else {
				pageStatus = 'confirmation'
			}
		}

		setNextIncompleteStepActive()
		await setPageState(pageStatus as PageStatusType, meta)
	}

	// This method will whether the planHolder has completed any of the PDFs
	const hasCompletedDocumentStep = (planHolderId: string) => {
		return steps.value[planHolderId].some(
			(s) => s.completed && s.type === 'document'
		)
	}

	// If the current planHolder has yet to complete all the required steps, this method will move to the next incomplete step for the current planHolder
	// Else it will move to the first incomplete step for the next plan holder
	// Else, after the last plan holder's last required step, it will move to Finish & Review,
	// even if an earlier plan holder has unfinalized steps, as the review page shows what is left
	const handleNextStep = async () => {
		await setStepCompleted(currentStep.value!, true)

		// Check if current step is a supplemental step for this plan holder
		const isSupplementalStep = supplementalSteps.value[
			currentPlanHolderID.value
		]?.some((step) => step.label === currentStep.value!.label)

		if (isSupplementalStep) {
			return
		}

		const nextIndex =
			steps.value[currentPlanHolderID.value].findIndex(
				(step) => step.label === currentStep.value!.label
			) + 1

		if (nextIndex === steps.value[currentPlanHolderID.value].length) {
			const planHolderIds = Object.keys(steps.value)
			const currentIndex = planHolderIds.indexOf(currentPlanHolderID.value)

			// If there's a next PlanHolder
			if (currentIndex < planHolderIds.length - 1) {
				const nextPlanHolderId = planHolderIds[currentIndex + 1]
				const nextPlanHolder = sessionStore.getPlanHolder(nextPlanHolderId)
				const firstStep = steps.value[nextPlanHolderId][0]

				if (firstStep.type === 'document') {
					const document = documentStore.getPlanHolderDocumentByType(
						nextPlanHolder as Plan,
						firstStep.label
					)

					if (document) {
						await setPageState('switch', {
							planHolderID: nextPlanHolderId,
							document: toRaw(document),
						})
					}
				} else {
					await setPageState(firstStep.label as PageStatusType, {
						planHolderID: nextPlanHolderId,
					})
				}

				setStepActive(nextPlanHolderId, firstStep)
				setCurrentPlanHolderID(nextPlanHolderId)

				// Expand the next person's section if collapsed
				const nextPersonToggle = document.getElementById(
					nextPlanHolderId + '-toggle'
				)
				const isNextPersonToggleExpanded =
					nextPersonToggle?.getAttribute('aria-expanded') === 'true'
				if (!isNextPersonToggleExpanded) {
					nextPersonToggle?.click()
				}
			} else {
				// Same as the Finish & Review button: FoxitManager saves the open document,
				// merges each packet, then sets the page state to complete
				setCurrentStepInactive()
				await setPageState('merging')
			}

			return
		}

		const nextStep = steps.value[currentPlanHolderID.value][nextIndex]

		if (nextStep.type === 'document') {
			const currentPlanHolder = sessionStore.getPlanHolder(
				currentPlanHolderID.value
			) as Plan
			const document = documentStore.getPlanHolderDocumentByType(
				currentPlanHolder,
				nextStep.label
			)

			if (document) {
				await setPageState('switch', {
					planHolderID: currentPlanHolderID.value,
					document: toRaw(document),
				})
			}
		} else {
			await setPageState(nextStep.label as PageStatusType, {
				planHolderID: currentPlanHolderID.value,
			})
		}

		setStepActive(currentPlanHolderID.value, nextStep)
	}

	// This method will progress the paymentStep to the Payment Authorization PDF or HTML step
	const setPaymentStepTo = async (step: 'html' | 'document') => {
		const updates = {
			paymentStep: step,
			completedSteps: currentPlan.value?.completedSteps,
		}
		if (step === 'html') {
			updates.completedSteps = currentPlan.value?.completedSteps.filter(
				(s) => s !== DocumentType.PaymentAuthorization
			)
		}
		await planStore.updatePlan(currentPlanHolderID.value, updates)
		await setPaymentStep(currentPlanHolderID.value, step)
	}

	// This method will progress the oapStep to the OAP Enrollment PDF or HTML step
	const setOapStepTo = async (step: 'html' | 'document') => {
		const updates = {
			oapStep: step,
			completedSteps: currentPlan.value?.completedSteps,
		}
		if (step === 'html') {
			updates.completedSteps = currentPlan.value?.completedSteps.filter(
				(s) => s !== DocumentType.OAPEnrollment
			)
		}
		await planStore.updatePlan(currentPlanHolderID.value, updates)
		await setOapEnrollmentStep(currentPlanHolderID.value, step)
	}

	// This method will progress the everplansStep to the PDF or HTML step
	const setEverplansStepTo = async (step: 'html' | 'document') => {
		const updates = {
			everplansStep: step,
			completedSteps: currentPlan.value?.completedSteps,
		}
		if (step === 'html') {
			updates.completedSteps = currentPlan.value?.completedSteps.filter(
				(s) => s !== DocumentType.EverplansEnrollment
			)
		}
		await planStore.updatePlan(currentPlanHolderID.value, updates)
		await setEverplansPaymentStep(currentPlanHolderID.value, step)
	}

	// This will return the form to an editable state and mark the step incomplete.
	const handleEditDocument = async () => {
		await setStepCompleted(currentStep.value!, false)

		if (currentStep.value!.type === 'document') {
			setStepValidated(currentStep.value!, false)
		}

		if (currentStep.value?.label === 'confirmation') {
			steps.value[currentPlanHolderID.value] = steps.value[
				currentPlanHolderID.value
			].map((step) => ({
				...step,
				completed: false,
				valid: false,
				autofill: false,
			}))

			await planStore.updatePlan(currentPlanHolderID.value, {
				completedSteps: [],
				everplansEmail: undefined,
			})
		}
	}

	// Temporarily only reflect missing FD signatures, will update this function to indicate different signers
	// see types.d.ts for proposed signers (e.g. Purchaser)
	const isMissingSignatures = (planId: string) => {
		return !!missingSignatures.value[planId]?.length
	}

	const clearMissingSignatures = () => {
		missingSignatures.value = {}
	}

	const setMissingSignatures = (
		planId: string,
		signatures: MissingSignaturesBySigner[]
	) => {
		missingSignatures.value[planId] = signatures
	}

	// used to set the selected FD to send the package to
	const setSelectedSigner = (
		planId: string,
		signer: SignerSelection | 'not-required' | undefined
	) => {
		selectedSigners.value[planId] = signer
	}

	/**
	 * This function determines whether a given plan needs their signer to be specified
	 * If the state does not require it, or no signatures are missing, no signer info is needed
	 * @param planId
	 */
	const planNeedsSigner = (planId: string) => {
		if (!isMissingSignatures(planId)) {
			return false
		}

		return isStateRequiresSignature(planId)
	}

	/**
	 * This method is used by NGL service to determine whether to send
	 * e-sign payload to the BE
	 * @param planId
	 */

	const shouldSendSignaturePayload = (planId: string) => {
		// if state doesn't want it we don't send it
		if (!isStateRequiresSignature(planId)) {
			return false
		}

		// if there's no signature missing, no point of sending payload
		if (!isMissingSignatures(planId)) {
			return false
		}

		// signer has to be valid and holds all fields that Backend needs
		const signer = selectedSigners.value[planId]
		if (signer === undefined || signer === 'not-required') {
			return false
		}
		const signerKeys = ['firstName', 'lastName', 'email']
		let signerIsValid = true

		for (const key of signerKeys) {
			if (!Object.hasOwn(signer, key)) {
				signerIsValid = false
			}
		}

		return signerIsValid
	}

	const getPlanState = (planId: string) => {
		const plan = planStore.getPlan(planId)
		if (!plan) {
			return undefined
		}
		return crmStore.findFuneralHome(plan.funeralHomeId)?.billingAddress
			?.stateCode
	}

	const isStateRequiresSignature = (planId: string) => {
		return stateRequiresSignature(getPlanState(planId), 'FuneralDirector')
	}

	// Watch for changes to the current plan's insurance payment type and reset payment step to 'html'
	watch(insurancePaymentType, async (newPaymentType, oldPaymentType) => {
		if (
			!oldPaymentType ||
			!stepsReady.value ||
			newPaymentType === oldPaymentType
		) {
			return
		}

		// Only trigger if payment type actually changed, and steps for current planHolder has loaded, then alter payment step to HTML
		await setPaymentStep(currentPlanHolderID.value, 'html', false)
	})

	return {
		currentDocument,
		currentPlanHolderID,
		currentStep,
		steps,
		supplementalSteps,
		pageState,
		currentPlan,
		currentPlanIsEncrypted,
		missingSignatures,
		selectedSigners,
		useAlternateFormClass,
		hasAlternateFormClass,
		planHolderIsEncrypted,
		isConfirmationCompleted,
		isEverplansCompleted,
		setCurrentDocument,
		setCurrentPlanHolderID,
		setPageState,
		setStepActive,
		setCurrentStepInactive,
		setSteps,
		setPaymentStep,
		setStepCompleted,
		setStepValidated,
		setNextIncompleteStepActive,
		getNextIncompleteStep,
		getStepByLabel,
		setPageStateOnLoad,
		hasCompletedDocumentStep,
		handleNextStep,
		setPaymentStepTo,
		setOapStepTo,
		setEverplansStepTo,
		handleEditDocument,
		setStepAutoFilled,
		setMissingSignatures,
		clearMissingSignatures,
		isMissingSignatures,
		setSelectedSigner,
		isStateRequiresSignature,
		shouldSendSignaturePayload,
		clearCurrentDocument,
		planNeedsSigner,
	}
})
